import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "reactstrap";
import { Plus, Trash2 } from "react-feather";
import moment from "moment";
import { toast } from "react-toastify";
import {
  addDemoOpsAgentShift,
  deleteDemoOpsAgentShift,
  getDemoOpsAgentDetails,
  getDemoOpsAgentShifts,
  getDemoOpsAgents,
  updateDemoOpsAgentShift,
  type DemoOpsAgentListItem,
  type DemoOpsShiftRow,
} from "../../../Api/apiUtils";
import { getDemoOpsSession } from "./demoOps.lib";
import { MixpanelEvents, trackEvent } from "../../../utils/mixpanelTracking";
import {
  getLoggedInAdminEmail,
  hasEmailAccess,
  useEmailAccessConfig,
} from "../../../utils/emailAccessConfig";
import {
  Button,
  Card,
  EmptyState,
  Field,
  Loader,
  Page,
  PageHeader,
  Select,
  Table,
  Toolbar,
} from "../../../ui";
import "./demo-ops.scss";

const DAY_ORDER = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

function sortShiftsByDay(shifts: DemoOpsShiftRow[]) {
  return [...shifts].sort(
    (a, b) => DAY_ORDER.indexOf(String(a.day)) - DAY_ORDER.indexOf(String(b.day))
  );
}

function formatTimeLabel(time?: string) {
  if (!time) return "—";
  const m = moment(`1970-01-01T${time}`);
  return m.isValid() ? m.format("h:mm:ss A") : time;
}

function timeSelectOptions(times: string[], placeholder?: string) {
  const opts = times.map((t) => ({ value: t, label: formatTimeLabel(t) }));
  return placeholder ? [{ value: "", label: placeholder }, ...opts] : opts;
}

function buildTimeOptions(currentTime?: string) {
  const options = new Set<string>();
  for (let i = 0; i < 48; i++) {
    const hours = Math.floor(i / 2);
    const minutes = (i % 2) * 30;
    options.add(
      `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`
    );
  }
  if (currentTime) options.add(currentTime);
  return Array.from(options);
}

type DemopsShiftProps = {
  visible?: boolean;
};

const DemopsShift: React.FC<DemopsShiftProps> = ({ visible = true }) => {
  const session = useMemo(() => getDemoOpsSession(), []);
  const emailAccessVersion = useEmailAccessConfig();

  const [agents, setAgents] = useState<DemoOpsAgentListItem[]>([]);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [shiftDetails, setShiftDetails] = useState<DemoOpsShiftRow[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [loadingShifts, setLoadingShifts] = useState(false);
  const [canEdit, setCanEdit] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [editedShifts, setEditedShifts] = useState<DemoOpsShiftRow[]>([]);
  const [saving, setSaving] = useState(false);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newShiftDay, setNewShiftDay] = useState("");
  const [newShiftFromTime, setNewShiftFromTime] = useState("");
  const [newShiftToTime, setNewShiftToTime] = useState("");
  const [adding, setAdding] = useState(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<DemoOpsShiftRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadCanEdit = useCallback(async () => {
    if (hasEmailAccess("demoOpsShiftEdit", getLoggedInAdminEmail())) {
      setCanEdit(true);
      return;
    }
    if (!session.agentId) {
      setCanEdit(false);
      return;
    }
    try {
      const details = await getDemoOpsAgentDetails(session.agentId);
      setCanEdit(details.role === "Demo-Ops-Admin");
    } catch (err) {
      console.error("Failed to load Demo Ops role", err);
      setCanEdit(false);
    }
  }, [session.agentId, emailAccessVersion]);

  const loadAgents = useCallback(async () => {
    setLoadingAgents(true);
    try {
      const data = await getDemoOpsAgents();
      setAgents(data);
    } catch (err) {
      console.error("Failed to fetch agents", err);
      toast.error("Failed to load agents");
    } finally {
      setLoadingAgents(false);
    }
  }, []);

  useEffect(() => {
    if (!visible) return;
    void loadCanEdit();
    void loadAgents();
  }, [visible, loadCanEdit, loadAgents]);

  const fetchShiftDetails = async (agentId: string) => {
    setSelectedAgent(agentId);
    setIsEditing(false);
    setEditedShifts([]);
    if (!agentId) {
      setShiftDetails([]);
      return;
    }
    setLoadingShifts(true);
    try {
      const data = await getDemoOpsAgentShifts(agentId);
      setShiftDetails(sortShiftsByDay(data));
    } catch (err) {
      console.error("Failed to fetch shift details", err);
      toast.error("Failed to load shift details");
      setShiftDetails([]);
    } finally {
      setLoadingShifts(false);
    }
  };

  const selectedAgentData = agents.find(
    (a) => String(a.agentId) === String(selectedAgent)
  );

  const handleAddShift = async () => {
    if (!canEdit) return;
    if (!newShiftDay || !newShiftFromTime || !newShiftToTime) {
      toast.error("Please fill in all fields.");
      return;
    }
    if (!selectedAgentData) {
      toast.error("Invalid agent selected.");
      return;
    }

    setAdding(true);
    try {
      await addDemoOpsAgentShift({
        agentId: selectedAgent,
        agentName: String(selectedAgentData.agentName ?? ""),
        role: "Demo-Ops",
        status: "active",
        day: newShiftDay,
        shiftStartTime: newShiftFromTime,
        shiftEndTime: newShiftToTime,
      });
      trackEvent(MixpanelEvents.DEMOOPS_SHIFT_ADD, {
        page: "DemoOps Shifts",
        agentId: selectedAgent,
        agentName: selectedAgentData.agentName,
        day: newShiftDay,
        shiftStartTime: newShiftFromTime,
        shiftEndTime: newShiftToTime,
      });
      toast.success("Shift added successfully.");
      setIsAddModalOpen(false);
      setNewShiftDay("");
      setNewShiftFromTime("");
      setNewShiftToTime("");
      await fetchShiftDetails(selectedAgent);
    } catch (err) {
      console.error("Failed to add new shift", err);
      toast.error("Failed to add shift");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async () => {
    if (!canEdit || !rowToDelete?.id) return;
    setDeleting(true);
    try {
      await deleteDemoOpsAgentShift(rowToDelete.id);
      trackEvent(MixpanelEvents.DEMOOPS_SHIFT_DELETE, {
        page: "DemoOps Shifts",
        agentId: selectedAgent,
        agentName: selectedAgentData?.agentName,
        deletedDay: rowToDelete.day,
        deletedShiftStartTime: rowToDelete.shiftStartTime,
        deletedShiftEndTime: rowToDelete.shiftEndTime,
        entryId: rowToDelete.id,
      });
      toast.success("Shift deleted successfully.");
      setIsDeleteModalOpen(false);
      setRowToDelete(null);
      await fetchShiftDetails(selectedAgent);
    } catch (err) {
      console.error("Failed to delete shift", err);
      toast.error("Failed to delete shift");
    } finally {
      setDeleting(false);
    }
  };

  const toggleEditMode = () => {
    if (!canEdit) return;
    if (!isEditing) {
      setEditedShifts(shiftDetails.map((s) => ({ ...s })));
      setIsEditing(true);
    } else {
      setIsEditing(false);
      setEditedShifts([]);
    }
  };

  const handleTimeChange = (
    id: string | number | undefined,
    field: "shiftStartTime" | "shiftEndTime",
    value: string
  ) => {
    setEditedShifts((prev) =>
      prev.map((shift) =>
        String(shift.id) === String(id) ? { ...shift, [field]: value } : shift
      )
    );
  };

  const saveAllChanges = async () => {
    if (!canEdit || !selectedAgentData) return;
    setSaving(true);
    try {
      await Promise.all(
        editedShifts.map((shift) =>
          updateDemoOpsAgentShift({
            agentId: shift.agentId ?? selectedAgent,
            entryId: shift.id as string | number,
            day: String(shift.day ?? ""),
            shiftStartTime: String(shift.shiftStartTime ?? ""),
            shiftEndTime: String(shift.shiftEndTime ?? ""),
          })
        )
      );
      editedShifts.forEach((shift) => {
        trackEvent(MixpanelEvents.DEMOOPS_SHIFT_UPDATE, {
          page: "DemoOps Shifts",
          agentId: selectedAgent,
          agentName: selectedAgentData.agentName,
          entryId: shift.id,
          day: shift.day,
          shiftStartTime: shift.shiftStartTime,
          shiftEndTime: shift.shiftEndTime,
        });
      });
      toast.success("Shifts updated successfully.");
      setIsEditing(false);
      setEditedShifts([]);
      await fetchShiftDetails(selectedAgent);
    } catch (err) {
      console.error("Failed to save changes", err);
      toast.error("Failed to save some changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page className="demo-ops">
      <PageHeader
        eyebrow="Demo Ops"
        title="Demo Shifts"
        subtitle="Weekly shift timings for each Demo Ops agent."
      />

      <Card
        title="Shifts"
        actions={
          <div className="demo-ops-card-actions">
            <Button
              variant={isEditing ? "secondary" : "primary"}
              icon={<Plus size={16} aria-hidden />}
              disabled={!selectedAgent || !canEdit}
              onClick={() => setIsAddModalOpen(true)}
            >
              Add New Shift
            </Button>
            <Button
              variant="secondary"
              disabled={!selectedAgent || !canEdit}
              onClick={toggleEditMode}
            >
              {isEditing ? "Cancel" : "Edit All"}
            </Button>
            {isEditing && (
              <Button
                variant="primary"
                disabled={!editedShifts.length}
                loading={saving}
                onClick={() => void saveAllChanges()}
              >
                Save All
              </Button>
            )}
          </div>
        }
      >
        <div className="ds-stack demo-ops-panel">
          <Toolbar>
            <Field label="Select Agent" id="demoOpsAgentDropdown">
              {(ctl) => (
                <Select
                  {...ctl}
                  value={selectedAgent}
                  disabled={loadingAgents}
                  onChange={(e) => void fetchShiftDetails(e.target.value)}
                  options={[
                    { value: "", label: "---Select Agent---" },
                    ...agents.map((agent) => ({
                      value: String(agent.agentId ?? ""),
                      label: String(agent.agentName ?? ""),
                    })),
                  ]}
                />
              )}
            </Field>
          </Toolbar>

          {loadingShifts ? (
            <Loader label="Loading shifts..." />
          ) : shiftDetails.length > 0 ? (
            <div className="demo-ops-scroll">
              <Table caption="Agent shifts">
                <thead>
                  <tr>
                    <th>Day</th>
                    <th>Shift Timings</th>
                    <th className="demo-shifts__delete-col">Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {shiftDetails.map((shift) => {
                    const edited = editedShifts.find(
                      (s) => String(s.id) === String(shift.id)
                    );
                    return (
                      <tr key={String(shift.id)}>
                        <td>{shift.day}</td>
                        <td>
                          {isEditing ? (
                            <div className="demo-shifts__time-range">
                              <Select
                                size="sm"
                                aria-label={`${shift.day ?? ""} start time`}
                                className="demo-shifts__time"
                                value={
                                  edited?.shiftStartTime ||
                                  shift.shiftStartTime ||
                                  ""
                                }
                                onChange={(e) =>
                                  handleTimeChange(
                                    shift.id,
                                    "shiftStartTime",
                                    e.target.value
                                  )
                                }
                                options={timeSelectOptions(
                                  buildTimeOptions(shift.shiftStartTime)
                                )}
                              />
                              <span>to</span>
                              <Select
                                size="sm"
                                aria-label={`${shift.day ?? ""} end time`}
                                className="demo-shifts__time"
                                value={
                                  edited?.shiftEndTime ||
                                  shift.shiftEndTime ||
                                  ""
                                }
                                onChange={(e) =>
                                  handleTimeChange(
                                    shift.id,
                                    "shiftEndTime",
                                    e.target.value
                                  )
                                }
                                options={timeSelectOptions(
                                  buildTimeOptions(shift.shiftEndTime)
                                )}
                              />
                            </div>
                          ) : shift.shiftStartTime && shift.shiftEndTime ? (
                            `${formatTimeLabel(shift.shiftStartTime)} to ${formatTimeLabel(shift.shiftEndTime)}`
                          ) : (
                            <span className="ds-text-subtle">No Data</span>
                          )}
                        </td>
                        <td>
                          {canEdit ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="demo-shifts__delete"
                              aria-label={`Delete ${shift.day ?? ""} shift`}
                              icon={<Trash2 size={16} aria-hidden />}
                              onClick={() => {
                                setRowToDelete(shift);
                                setIsDeleteModalOpen(true);
                              }}
                            />
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          ) : selectedAgent ? (
            <EmptyState
              title="No shifts for this agent"
              description={
                canEdit
                  ? "Use Add New Shift to set this agent's working hours."
                  : "Shifts appear here once a Demo Ops admin adds them."
              }
            />
          ) : (
            <EmptyState
              title="Select an agent to view shifts"
              description="Pick an agent above to see their weekly shift timings."
            />
          )}
        </div>
      </Card>

      <Modal
        isOpen={isAddModalOpen}
        toggle={() => setIsAddModalOpen(false)}
        centered
        className="ds-dialog"
      >
        <h2 className="ds-dialog__title">Add New Shift</h2>
        <div className="demo-shifts__form">
          <Field label="Day">
            {(ctl) => (
              <Select
                {...ctl}
                value={newShiftDay}
                onChange={(e) => setNewShiftDay(e.target.value)}
                options={[
                  { value: "", label: "Select Day" },
                  ...DAY_ORDER.map((day) => ({ value: day, label: day })),
                ]}
              />
            )}
          </Field>
          <Field label="From Time">
            {(ctl) => (
              <Select
                {...ctl}
                value={newShiftFromTime}
                onChange={(e) => setNewShiftFromTime(e.target.value)}
                options={timeSelectOptions(buildTimeOptions(), "Select Time")}
              />
            )}
          </Field>
          <Field label="To Time">
            {(ctl) => (
              <Select
                {...ctl}
                value={newShiftToTime}
                onChange={(e) => setNewShiftToTime(e.target.value)}
                options={timeSelectOptions(buildTimeOptions(), "Select Time")}
              />
            )}
          </Field>
        </div>
        <div className="ds-dialog__actions">
          <Button variant="secondary" onClick={() => setIsAddModalOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={adding}
            onClick={() => void handleAddShift()}
          >
            Add Shift
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        toggle={() => setIsDeleteModalOpen(false)}
        centered
        className="ds-dialog"
      >
        <h2 className="ds-dialog__title">Confirm Deletion</h2>
        <div className="ds-dialog__body">Are you sure you want to delete this shift?</div>
        <div className="ds-dialog__actions">
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={deleting}
            onClick={() => void handleDelete()}
          >
            Confirm
          </Button>
        </div>
      </Modal>
    </Page>
  );
};

export default DemopsShift;
